import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus,
  XCircle,
  Loader2,
  CheckCircle,
  Clock,
  AlertTriangle,
  Truck,
  RotateCcw,
  Trash2
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fmtTz } from "../lib/time";

const OOS_REASONS = [
  { value: 'failed_inspection', label: 'Failed Inspection' },
  { value: 'awaiting_repair', label: 'Awaiting Repair' },
  { value: 'end_of_life', label: 'End of Life' },
  { value: 'other', label: 'Other' }
];

export default function AdminOutOfService() {
  const { authAxios, user } = useAuth();
  const [records, setRecords] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [processingId, setProcessingId] = useState(null);

  const isAdmin = user?.role === 'admin';

  const [formData, setFormData] = useState({
    equipment_id: '',
    reason: 'awaiting_repair',
    reason_details: '',
    estimated_return_date: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [oosRes, equipRes] = await Promise.all([
        authAxios.get('/equipment/out-of-service'),
        authAxios.get('/equipment')
      ]);
      setRecords(oosRes.data);
      setEquipment(equipRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      equipment_id: '',
      reason: 'awaiting_repair',
      reason_details: '',
      estimated_return_date: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.equipment_id) {
      toast.error('Please select equipment');
      return;
    }

    setSubmitting(true);
    try {
      await authAxios.post('/equipment/out-of-service', {
        ...formData,
        estimated_return_date: formData.estimated_return_date || null
      });
      toast.success('Equipment marked as out of service');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create record');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveReturn = async (oosId) => {
    if (!window.confirm('Approve this equipment to return to service?')) return;
    
    setProcessingId(oosId);
    try {
      await authAxios.post(`/equipment/out-of-service/${oosId}/return`);
      toast.success('Equipment returned to service');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve return');
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700 border-amber-200',
      approved_return: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      retired: 'bg-stone-100 text-stone-600 border-stone-200'
    };
    const icons = {
      pending: <Clock className="w-3 h-3 mr-1" />,
      approved_return: <CheckCircle className="w-3 h-3 mr-1" />,
      retired: <XCircle className="w-3 h-3 mr-1" />
    };
    return (
      <Badge variant="outline" className={`${styles[status]} flex items-center`}>
        {icons[status]}
        {status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </Badge>
    );
  };

  const getReasonBadge = (reason) => {
    const colors = {
      failed_inspection: 'bg-rose-100 text-rose-700',
      awaiting_repair: 'bg-amber-100 text-amber-700',
      end_of_life: 'bg-stone-100 text-stone-700',
      other: 'bg-blue-100 text-blue-700'
    };
    return (
      <Badge className={colors[reason]}>
        {reason?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </Badge>
    );
  };

  const filteredRecords = records.filter(r => {
    if (activeTab === 'all') return true;
    return r.status === activeTab;
  });

  const counts = {
    all: records.length,
    pending: records.filter(r => r.status === 'pending').length,
    approved_return: records.filter(r => r.status === 'approved_return').length,
    retired: records.filter(r => r.status === 'retired').length
  };

  // Equipment available to mark out of service (active only)
  const activeEquipment = equipment.filter(e => e.status === 'active');

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="out-of-service">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 font-heading">Out of Service Tracking</h1>
          <p className="text-stone-500 mt-1">Track equipment removed from service</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="mark-oos-btn">
              <Plus className="w-5 h-5 mr-2" />
              Mark Out of Service
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-rose-600" />
                Mark Equipment Out of Service
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Equipment *</Label>
                <Select value={formData.equipment_id} onValueChange={(v) => setFormData({ ...formData, equipment_id: v })}>
                  <SelectTrigger data-testid="oos-equipment-select">
                    <SelectValue placeholder="Select equipment" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeEquipment.length === 0 ? (
                      <SelectItem value="no-equipment" disabled>No active equipment available</SelectItem>
                    ) : (
                      activeEquipment.map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.name} ({e.equipment_id})</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Reason *</Label>
                <Select value={formData.reason} onValueChange={(v) => setFormData({ ...formData, reason: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OOS_REASONS.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Reason Details</Label>
                <Textarea
                  value={formData.reason_details}
                  onChange={(e) => setFormData({ ...formData, reason_details: e.target.value })}
                  placeholder="Provide additional details about why this equipment is out of service..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Estimated Return Date</Label>
                <Input
                  type="date"
                  value={formData.estimated_return_date}
                  onChange={(e) => setFormData({ ...formData, estimated_return_date: e.target.value })}
                />
              </div>

              <DialogFooter>
                <Button type="submit" disabled={submitting} className="bg-rose-600 hover:bg-rose-500">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                  Mark Out of Service
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-0 shadow-lg">
          <CardContent className="p-4 text-center">
            <XCircle className="w-8 h-8 mx-auto text-amber-600 mb-2" />
            <p className="text-2xl font-bold">{counts.pending}</p>
            <p className="text-sm text-stone-500">Pending</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg">
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
            <p className="text-2xl font-bold">{counts.approved_return}</p>
            <p className="text-sm text-stone-500">Returned</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg">
          <CardContent className="p-4 text-center">
            <AlertTriangle className="w-8 h-8 mx-auto text-stone-600 mb-2" />
            <p className="text-2xl font-bold">{counts.retired}</p>
            <p className="text-sm text-stone-500">Retired</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg">
          <CardContent className="p-4 text-center">
            <Truck className="w-8 h-8 mx-auto text-blue-600 mb-2" />
            <p className="text-2xl font-bold">{counts.all}</p>
            <p className="text-sm text-stone-500">Total Records</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6 bg-stone-100">
          <TabsTrigger value="all" className="data-[state=active]:bg-white">All ({counts.all})</TabsTrigger>
          <TabsTrigger value="pending" className="data-[state=active]:bg-white">Pending ({counts.pending})</TabsTrigger>
          <TabsTrigger value="approved_return" className="data-[state=active]:bg-white">Returned ({counts.approved_return})</TabsTrigger>
          <TabsTrigger value="retired" className="data-[state=active]:bg-white">Retired ({counts.retired})</TabsTrigger>
        </TabsList>

        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <XCircle className="w-5 h-5 text-stone-400" />
              Out of Service Records
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
              </div>
            ) : filteredRecords.length === 0 ? (
              <div className="text-center py-12 text-stone-500">
                <XCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No out of service records found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-stone-100">
                      <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Equipment</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Reason</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Details</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Reported By</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Est. Return</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Status</TableHead>
                      {isAdmin && <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRecords.map((record) => (
                      <TableRow key={record.id} className="border-stone-100 hover:bg-stone-50">
                        <TableCell>
                          <div>
                            <p className="font-medium">{record.equipment_name}</p>
                            <p className="text-sm text-stone-500">{record.equipment_code}</p>
                          </div>
                        </TableCell>
                        <TableCell>{getReasonBadge(record.reason)}</TableCell>
                        <TableCell className="max-w-[200px] truncate text-stone-600">
                          {record.reason_details || '-'}
                        </TableCell>
                        <TableCell className="text-stone-600">{record.reported_by_name}</TableCell>
                        <TableCell>
                          {record.estimated_return_date 
                            ? fmtTz(record.estimated_return_date, 'MMM d, yyyy') 
                            : '-'}
                        </TableCell>
                        <TableCell>{getStatusBadge(record.status)}</TableCell>
                        {isAdmin && (
                          <TableCell className="text-right space-x-1 whitespace-nowrap">
                            {record.status === 'pending' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleApproveReturn(record.id)}
                                disabled={processingId === record.id}
                                className="text-emerald-600 hover:bg-emerald-50"
                                data-testid={`return-${record.id}`}
                              >
                                {processingId === record.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                ) : (
                                  <RotateCcw className="w-4 h-4 mr-1" />
                                )}
                                Return to Service
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={async () => {
                                if (!window.confirm('Delete this Out-of-Service record permanently?')) return;
                                try {
                                  await authAxios.delete(`/equipment/out-of-service/${record.id}`);
                                  toast.success('Record deleted');
                                  fetchData();
                                } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
                              }}
                              data-testid={`delete-oos-${record.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-rose-600" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </Tabs>
    </div>
  );
}
